// Alzheimer Report Configuration
// Contains biomarker data, disease classifications, and recommendations

export const biomarkerData = {
  'pTau-217': {
    range: '0-26 pg/mL',
    maxRange: 26,
    interpretations: {
      Normal: 'No biochemical evidence of Alzheimer\'s Disease related tau changes in blood.',
      'Mild elevation': 'Borderline elevation of pTau-217 could reflect early changes of Alzheimer\'s Disease.',
      Elevated: 'An elevated pTau-217 level is associated with Alzheimer\'s disease–type tau pathology.'
    }
  },
  NfL: {
    range: '0-26 pg/mL',
    maxRange: 26,
    interpretations: {
      Normal: 'No evidence of ongoing neuronal injury or neurodegeneration.',
      'Mild elevation': 'Mild elevation in NfL is nonspecific and could reflect age-related changes, inflammation, or early neurodegeneration',
      Elevated: 'Elevated NfL indicates active axonal injury. This marker is not specific to Alzheimer\'s disease. Follow-up imaging and neurologist consultation may be considered.'
    }
  },
  ApoE4: {
    range: '0-100 mg/dL',
    maxRange: 100,
    interpretations: {
      Normal: 'Low ApoE4 protein concentration is consistent with non-carrier status. Associated with a low genetic risk for Alzheimer\'s Disease.',
      'Mild elevation': 'Intermediate levels of ApoE4 protein concentration is suggestive of possible APOE4 heterozygous expression (atleast one copy of APOE4 allele). Associated with moderately increased genetic risk for Alzheimer\'s Disease.',
      Elevated: 'High ApoE4 protein concentration is suggestive of possible APOE4 homozygous expression (two copies of APOE4 allele). Associated with elevated genetic risk for Alzheimer\'s Disease.'
    }
  },
  MoCA: {
    range: '0-30',
    maxRange: 30,
    interpretations: {
      High: 'Your score suggests your cognition is within the expected range for your age and education level.',
      Low: 'Your score suggests mild difficulty in memory encoding and delayed recall.'
    }
  }
};

export const alzheimerClassifications = {
  preclinicalNormal: {
    name: 'Preclinical Alzheimer\'s Disease',
    classification: 'Preclinical Alzheimer\'s Disease<br>(Detectable Alzheimer\'s Disease biology but with Normal cognition)',
    recommendations: [
      'Monitor closely. Repeat biomarkers in 6 months to check progression.',
      'Consult with your Neurologist for further course of action.',
      'Consider confirmatory testing (MRI ± amyloid PET/CSF) if clinically appropriate'
    ]
  },

  preclinicalGeneticRisk: {
    name: 'Preclinical Alzheimer\'s Disease with Genetic Risk',
    classification: 'Preclinical Alzheimer\'s Disease<br>(Detectable Alzheimer\'s Disease biology but with Normal cognition). Evidence of Neurodegeneration. Genetic Risk also present.',
    recommendations: [
      'Monitor closely. Repeat biomarkers in 6 to 12 months and compare with baseline.',
      'Consult with your Neurologist for further course of action.',
      'Consider confirmatory testing (MRI ± amyloid PET/CSF) if clinically appropriate'
    ]
  },

  mildCognitiveImpairment: {
    name: 'Mild Cognitive Impairment',
    classification: 'Mild cognitive impairment with likelihood of Alzheimer\'s Disease biology',
    recommendations: [
      'Monitor closely. Repeat biomarkers in 6 months to check progression.',
      'Consult with your Neurologist for further course of action.',
      'Consider brain MRI to assess structural changes if clinically appropriate'
    ]
  },

  prodromalAlzheimers: {
    name: 'Prodromal Alzheimer\'s Disease',
    classification: 'Prodromal Alzheimer\'s Disease (Mild cognitive impairment with detectable Alzheimer\'s Disease biology). Evidence of Neurodegeneration. Genetic Risk also present.',
    recommendations: [
      'Monitor closely. Repeat biomarkers in 6 months to check progression.',
      'Consult with your Neurologist for further course of action.',
      'Consider brain MRI to assess structural changes if clinically appropriate'
    ]
  },

  noCognitiveRisk: {
    name: 'No Cognitive Risk',
    classification: 'No Cognitive Risk. No Alzheimer\'s Disease biology. Normal Cognition',
    recommendations: [
      'Maintain brain-healthy lifestyle (exercise, good sleep & nutrition, cognitive engagement)',
      'Repeat screening in 24 months',
      'Continue routine health maintenance'
    ]
  },

  geneticRisk: {
    name: 'Genetic Risk',
    classification: 'Genetic Risk. No detectable Alzheimer\'s Disease biology. Normal Cognition',
    recommendations: [
      'Focus on modifiable risk factors (cardio-metabolic health, inflammation, sleep, exercise)',
      'Repeat biomarkers in 18 months and compare with baseline',
      'Consult with your Neurologist for further course of action'
    ]
  },

  cognitiveImpairmentNoAD: {
    name: 'Cognitive Impairment',
    classification: 'Cognitive Impairment with No detectable Alzheimer\'s Disease biology',
    recommendations: [
      'Maintain brain-healthy lifestyle (exercise, good sleep & nutrition, cognitive engagement)',
      'Consult with your Neurologist for further course of action',
      'Repeat biomarkers in 6 to 12 months'
    ]
  },

  cognitiveImpairmentWithGeneticRisk: {
    name: 'Cognitive Impairment with Genetic Risk',
    classification: 'Cognitive Impairment with non-specific neurodegeneration and No detectable Alzheimer\'s Disease biology. Genetic Risk also present.',
    recommendations: [
      'Maintain brain-healthy lifestyle (exercise, good sleep & nutrition, cognitive engagement)',
      'Consult with your Neurologist for further course of action',
      'Consider brain MRI to assess structural changes if clinically appropriate'
    ]
  },

  alzheimersDementia: {
    name: 'Alzheimer\'s Disease Dementia',
    classification: 'Alzheimer\'s Disease Dementia (Significant cognitive impairment with detectable Alzheimer\'s Disease biology)',
    recommendations: [
      'Monitor closely. Repeat biomarkers in 6 months to check progression.',
      'Consult with your Neurologist for further course of action.',
      'Consider confirmatory testing (MRI ± amyloid PET/CSF) if clinically appropriate'
    ]
  }
};
